<template>
    <div class="page-content-wrapper">
        <h3 class="title">{{ $t("tables.tables") }}</h3>
        
        <!-- Info message for paste feature -->
        <div v-if="hasTableInClipboard" class="paste-info-message">
            <i class="material-icons">content_paste</i>
            <span>Panoda kopyaladığınız tablo tespit edildi! Hemen <strong>Ctrl+V</strong> ile yapıştırarak yeni tablo oluşturun.</span>
        </div>

        <div class="table-wrapper">
            <div class="toolbar">
                <div class="left">
                    <a-input-search
                        style="width: 300px; margin: unset !important"
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input"
                        @input="onSearch"
                    />
                </div>
                <div class="right">
                    <PermissionController :permissions="['table.create']">
                        <SupsisButton @click="performShowCreateCategoryModal" class="category-btn">
                            <i class="material-icons">category</i>
                            Kategori Ekle
                        </SupsisButton>
                        <SupsisButton primary @click="$feature.beforeCreateTable(createTable)" class="new-table-button">
                            <i class="material-icons">add</i>
                            {{ $t("message.newTable") }}
                        </SupsisButton>
                        <SupsisButton class="ai-suggest-button" @click="showWizard">
                            <img src="/static/img/copilot.png" class="copilot-icon" />
                            {{ $t("tables.createAppWithSupsisAssistant") }}
                        </SupsisButton>
                    </PermissionController>
                </div>
            </div>

            <div class="categories-container">
                <div
                    v-for="category in categorizedTables"
                    :key="category.name"
                    class="category-section"
                    :class="{ 'drag-over': dragOverCategory === category.name }"
                    @drop="onDrop($event, category.name)"
                    @dragover="allowDrop($event, category.name)"
                    @dragleave="onDragLeave"
                >
                    <div class="category-header" @click="toggleCategory(category.name)">
                        <div class="category-info">
                            <SupsisButton
                                ghost
                                small
                                @click.stop="toggleCategory(category.name)"
                                class="collapse-toggle"
                            >
                                <i class="material-icons">
                                    {{ categoryExpandStates[category.name] !== false ? "expand_less" : "expand_more" }}
                                </i>
                            </SupsisButton>
                            <i v-if="category.icon" class="material-icons category-icon">{{ category.icon }}</i>
                            <i v-else class="material-icons category-icon">folder</i>
                            <span class="category-name">{{ category.name }}</span>
                            <span class="table-count">({{ category.tables.length }})</span>
                        </div>
                        <div class="category-actions">
                            <SupsisButton
                                v-if="category.name !== 'Genel'"
                                ghost
                                icon="edit"
                                small
                                @click.stop="editCategory(category)"
                                v-tippy="{ content: 'Kategoriyi Düzenle' }"
                            />
                            <SupsisButton
                                v-if="category.name !== 'Genel'"
                                ghost
                                icon="delete"
                                small
                                danger
                                @click.stop="deleteCategory(category.name)"
                                v-tippy="{ content: 'Kategoriyi Sil' }"
                            />
                            <a-popover
                                v-if="category.tables.length > 0"
                                trigger="click"
                                placement="bottomRight"
                                overlayClassName="category-actions-dropdown"
                                :visible="categoryDropdownStates[category.name]"
                                @visibleChange="
                                    (visible) => {
                                        $set(categoryDropdownStates, category.name, visible);
                                    }
                                "
                            >
                                <template slot="content">
                                    <div class="dropdown-menu-content">
                                        <div class="dropdown-menu-item submenu-item">
                                            <span class="material-icons">drive_file_move</span>
                                            <span>Tüm Tabloları Taşı</span>
                                                                                         <span class="material-icons submenu-arrow">chevron_left</span>
                                            <div class="submenu-content">
                                                <div 
                                                    v-for="targetCategory in availableCategoriesForCategory(category.name)"
                                                    :key="targetCategory.name"
                                                    class="submenu-item-content"
                                                    @click="moveAllTablesToCategory(category.name, targetCategory.name)"
                                                >
                                                    <i v-if="targetCategory.icon" class="material-icons">{{ targetCategory.icon }}</i>
                                                    <i v-else class="material-icons">folder</i>
                                                    <span>{{ targetCategory.name }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <SupsisButton 
                                    ghost 
                                    icon="more_vert" 
                                    small 
                                    @click.stop 
                                    class="category-more-actions-btn" 
                                />
                            </a-popover>
                        </div>
                    </div>

                    <div
                        class="tables-grid"
                        v-show="categoryExpandStates[category.name] !== false"
                        :class="{ collapsed: categoryExpandStates[category.name] === false }"
                    >
                        <div v-if="category.tables.length === 0" class="empty-category-message">
                            <i class="material-icons">drag_handle</i>
                            <span>Tabloları buraya sürükleyerek kategoriye ekleyebilirsiniz</span>
                        </div>
                        <div
                            v-for="table in category.tables"
                            :key="table._id"
                            class="table-card"
                            :class="{ dragging: draggedTable && draggedTable._id === table._id }"
                            draggable="true"
                            @dragstart="onDragStart($event, table)"
                            @dragend="onDragEnd"
                        >
                            <div class="table-card-header">
                                <a class="table-name" :href="getTableHref(table._id)" @click="handleTableClick(table._id, $event)">{{ table.name }}</a>
                                <div class="table-actions">
                                    <SupsisButton
                                        component="a"
                                        :href="getTableHref(table._id)"
                                        @click="handleTableClick(table._id, $event)"
                                        class="view-btn"
                                        ghost
                                        icon="visibility"
                                        small
                                    />
                                    <PermissionController :permissions="['update']">
                                        <SupsisButton
                                            v-if="canEditTableMeta(table)"
                                            class="edit-btn"
                                            @click="editTable(table._id)"
                                            ghost
                                            icon="edit"
                                            small
                                        />
                                    </PermissionController>
                                    <PermissionController :permissions="['table.delete']">
                                        <SupsisButton
                                            @click="deleteTable(table._id)"
                                            danger
                                            ghost
                                            icon="delete"
                                            small
                                            :disabled="table.deleteProtection"
                                            v-tippy="{
                                                content: table.deleteProtection ? 'Silme koruması aktif' : '',
                                            }"
                                        />
                                    </PermissionController>
                                    <a-popover
                                        trigger="click"
                                        placement="bottomRight"
                                        overlayClassName="table-actions-dropdown"
                                        :visible="dropdownStates[table._id]"
                                        @visibleChange="
                                            (visible) => {
                                                $set(dropdownStates, table._id, visible);
                                            }
                                        "
                                    >
                                        <template slot="content">
                                            <div class="dropdown-menu-content">
                                                <div 
                                                    v-if="canAccessTable(table)"
                                                    class="dropdown-menu-item" 
                                                    @click="duplicateTable(table)"
                                                >
                                                    <span class="material-icons">content_copy</span>
                                                    <span>Tabloyu Kopyala</span>
                                                </div>
                                                <div 
                                                    v-if="canAccessTable(table)"
                                                    class="dropdown-menu-item" 
                                                    @click="copyTableAsJSON(table)"
                                                >
                                                    <span class="material-icons">file_copy</span>
                                                    <span>JSON Olarak Kopyala</span>
                                                </div>
                                                <div 
                                                    v-if="canEditTableMeta(table)"
                                                    class="dropdown-menu-item submenu-item"
                                                >
                                                    <span class="material-icons">drive_file_move</span>
                                                    <span>Kategori Değiştir</span>
                                                    <span class="material-icons submenu-arrow">chevron_left</span>
                                                    <div class="submenu-content">
                                                        <div 
                                                            v-for="categoryOption in availableCategoriesForTable(table)"
                                                            :key="categoryOption.name"
                                                            class="submenu-item-content"
                                                            @click="moveTableToCategory(table, categoryOption.name)"
                                                        >
                                                            <i v-if="categoryOption.icon" class="material-icons">{{ categoryOption.icon }}</i>
                                                            <i v-else class="material-icons">folder</i>
                                                            <span>{{ categoryOption.name }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div 
                                                    v-if="canEditTableMeta(table)"
                                                    class="dropdown-menu-item" 
                                                    @click="toggleDeleteProtection(table)"
                                                >
                                                    <span class="material-icons">
                                                        {{ table.deleteProtection ? "lock_open" : "lock" }}
                                                    </span>
                                                    <span>
                                                        {{
                                                            table.deleteProtection
                                                                ? "Silme Korumasını Kapat"
                                                                : "Silme Korumasını Aç"
                                                        }}
                                                    </span>
                                                </div>
                                                <div 
                                                    v-if="canShowPermissionSettings(table)" 
                                                    class="dropdown-menu-item" 
                                                    @click="goToPermissionSettings(table)"
                                                >
                                                    <span class="material-icons">admin_panel_settings</span>
                                                    <span>Gizlilik Ayarları</span>
                                                </div>
                                            </div>
                                        </template>
                                        <SupsisButton ghost icon="more_vert" small class="more-actions-btn" />
                                    </a-popover>
                                </div>
                            </div>

                            <div class="table-description" v-if="table.description">
                                {{ table.description }}
                            </div>

                            <div class="table-meta">
                                <div class="created-by" v-if="table.createdByUser">
                                    <Avatar
                                        :size="20"
                                        :src="
                                            users[table.createdByUser._id]
                                                ? users[table.createdByUser._id].gravatar
                                                : null
                                        "
                                        :username="
                                            users[table.createdByUser._id]
                                                ? users[table.createdByUser._id].name
                                                : 'Sistem'
                                        "
                                    />
                                    <span>{{
                                        users[table.createdByUser._id] ? users[table.createdByUser._id].name : "Sistem"
                                    }}</span>
                                </div>
                                <div class="created-date">
                                    {{ $moment(table.createdAt).format("DD.MM.YYYY") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create Table Modal -->
        <Modal2
            v-if="showCreateModal"
            @cancel="showCreateModal = false"
            :showFooter="false"
            :title="$t('tables.createTable')"
        >
            <CreateTable @create="handleCreateTable" />
        </Modal2>

        <!-- Edit Table Modal -->
        <Modal2
            v-if="showEditModal"
            @cancel="showEditModal = false"
            :showFooter="false"
            :title="$t('tables.editTable')"
        >
            <EditTable :table="editingTable" @update="handleUpdateTable" />
        </Modal2>

        <!-- Create/Edit Category Modal -->
        <Modal2
            v-if="showCreateCategoryModal || showEditCategoryModal"
            @cancel="closeCategoryModal"
            :showFooter="false"
            :title="editingCategory ? 'Kategori Düzenle' : 'Kategori Ekle'"
        >
            <div class="table-category-form">
                <div class="form-group">
                    <label>Kategori Adı</label>
                    <a-input v-model="categoryForm.name" placeholder="Kategori adını girin" />
                </div>
                <div class="form-group">
                    <label>İkon</label>
                    <MaterialIconPicker v-model="categoryForm.icon" />
                </div>
                <div class="form-actions d-flex gap-5 flex-end">
                    <SupsisButton @click="closeCategoryModal">İptal</SupsisButton>
                    <SupsisButton primary @click="saveCategory">
                        {{ editingCategory ? "Güncelle" : "Oluştur" }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>

        <TableAiWizard v-if="showTableAiWizard" @table-added="tableAdded" @close="showTableAiWizard = false" />
    </div>
</template>

<script>
import { TABLE_API } from "../../../api/table";
import SupsisTable from "../../../components/SupsisTable.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import CreateTable from "./CreateTable.vue";
import EditTable from "./EditTable.vue";
import { mapActions, mapState } from "vuex";
import TableAiWizard from "./TableAiWizard.vue";
import Avatar from "vue-avatar";
import PermissionController from "../../../components/permission-controller/PermissionController";
import MaterialIconPicker from "../Module/MaterialIconPicker.vue";
import { canEditTablePermissions, canAccessTable, getUserDepartments, checkTablePermission } from "../../../utils/TablePermissionUtils";
import { TABLE_PERMISSION_SCOPES } from "../../../utils/TablePermissionScopes";

export default {
    name: "TableList",
    components: {
        PermissionController,
        TableAiWizard,
        SupsisTable,
        SupsisButton,
        Modal2,
        CreateTable,
        EditTable,
        Avatar,
        MaterialIconPicker,
    },
            data() {
        return {
            searchText: "",
            categories: [], // Separate array to track created categories
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
            loading: false,
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            showCreateModal: false,
            newTable: {
                name: "",
                description: "",
            },
            showEditModal: false,
            editingTable: null,
            showTableAiWizard: false,
            dropdownStates: {}, // Track dropdown visibility states
            categoryDropdownStates: {}, // Track category dropdown visibility states
            showCreateCategoryModal: false,
            showEditCategoryModal: false,
            editingCategory: null,
            categoryForm: {
                name: "",
                icon: "",
            },
            draggedTable: null,
            dragOverCategory: null,
            categoryExpandStates: {},
            hasTableInClipboard: false,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            tables: (state) => state.table.tables,
        }),
        isRootUser() {
            return this.adminUser || (this.user && this.user.role && this.user.role.isRoot);
        },
        userDepartments() {
            return getUserDepartments(this.user);
        },
        filteredTables() {
            // Önce erişim kontrolü yap
            let accessibleTables = this.tables.filter((table) => {
                return canAccessTable(table, this.user, this.adminUser, this.userDepartments);
            });

            // Sonra arama filtresi uygula
            if (!this.searchText) {
                return accessibleTables;
            }
            const searchLower = this.searchText.toLowerCase();
            return accessibleTables.filter(
                (table) =>
                    table.name.toLowerCase().includes(searchLower) ||
                    (table.description || "").toLowerCase().includes(searchLower)
            );
        },
        paginatedTables() {
            const start = (this.pagination.page - 1) * this.pagination.perPage;
            const end = start + this.pagination.perPage;
            return this.filteredTables.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.filteredTables.length / this.pagination.perPage);
        },
        categorizedTables() {
            const categories = {};

            // First, add all categories from tables
            this.filteredTables.forEach((table) => {
                const categoryName = table.category && table.category.name ? table.category.name : "Genel";
                const categoryIcon = table.category && table.category.icon ? table.category.icon : null;

                if (!categories[categoryName]) {
                    categories[categoryName] = {
                        name: categoryName,
                        icon: categoryIcon,
                        tables: [],
                    };
                }
                categories[categoryName].tables.push(table);
            });

            // Then, add separately created categories (even if they're empty)
            this.categories.forEach((category) => {
                if (!categories[category.name]) {
                    categories[category.name] = {
                        name: category.name,
                        icon: category.icon,
                        tables: [],
                    };
                } else {
                    // Update icon if category exists from tables but has different icon
                    categories[category.name].icon = category.icon;
                }
            });

            // Always ensure "Genel" category exists
            if (!categories["Genel"]) {
                categories["Genel"] = {
                    name: "Genel",
                    icon: null,
                    tables: [],
                };
            }

            // Sort categories: "Genel" always first, then alphabetically
            const sortedCategories = Object.values(categories).sort((a, b) => {
                if (a.name === "Genel") return -1;
                if (b.name === "Genel") return 1;
                return a.name.localeCompare(b.name);
            });

            return sortedCategories;
        },
    },
    methods: {
        ...mapActions({
            syncTables: "table/syncTables",
            deleteCustomerTable: "table/deleteCustomerTable",
        }),
        tableAdded(table) {
            // Vuex sync edilecek
            this.syncTables(0);
        },
        showWizard() {
            this.showTableAiWizard = true;
        },

        async fetchTables() {
            // Arka planda sunucudan sync et
            try {
                await this.syncTables(0);
            } catch (error) {
                console.error("Error syncing tables:", error);
            }
        },
        onSearch() {
            this.pagination.page = 1;
        },
        createTable() {
            this.showCreateModal = true;
        },
        async handleCreateTable(data) {
            try {
                const response = await TABLE_API.createTable(data.name, data.description, data.template);
                this.showCreateModal = false;
                // Vuex sync et
                await this.syncTables(0);
                this.viewTable(response._id);
            } catch (error) {
                console.error("Error creating table:", error);
            }
        },
        viewTable(tableId) {
            // Erişim kontrolü
            const table = this.tables.find((t) => t._id === tableId);
            if (table && !canAccessTable(table, this.user, this.adminUser, this.userDepartments)) {
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            
            this.$router.push(`/tables/t/${tableId}`);
        },
        getTableHref(tableId) {
            return `#/tables/t/${tableId}`;
        },
        handleTableClick(tableId, event) {
            // Erişim kontrolü
            const table = this.tables.find((t) => t._id === tableId);
            if (table && !canAccessTable(table, this.user, this.adminUser, this.userDepartments)) {
                event.preventDefault();
                this.$message.error(this.$t("tables.permissions.noPermission"));
                return;
            }
            
            // Command/Ctrl + Click durumunda tarayıcının varsayılan davranışına izin ver
            if (event.metaKey || event.ctrlKey) {
                return; // Tarayıcının varsayılan davranışına izin ver
            } else {
                // Normal tıklama durumunda varsayılan davranışı engelle ve router kullan
                event.preventDefault();
                this.viewTable(tableId);
            }
        },
        async deleteTable(tableId) {
            try {
                const table = this.tables.find((t) => t._id === tableId);

                // Check if delete protection is enabled
                if (table && table.deleteProtection) {
                    this.$message.warning(
                        "Bu tablo silme koruması ile korunmaktadır. Silmek için önce korumasını kaldırın."
                    );
                    return;
                }

                await this.$confirm({
                    title: this.$t("tables.deleteTable"),
                    content: this.$t("tables.sureDeleteTable"),
                    okText: this.$t("message.yes"),
                    okType: "danger",
                    cancelText: this.$t("message.no"),
                    onOk: async () => {
                        await this.deleteCustomerTable(tableId);
                        this.$message.success(this.$t("tables.deleteTableSucs"));
                    },
                });
            } catch (error) {
                console.error(this.$t("tables.deleteTableError"), error);
            }
        },
        async toggleDeleteProtection(table) {
            try {
                // Yetki kontrolü
                if (!checkTablePermission(table, TABLE_PERMISSION_SCOPES.TABLE_META_EDIT, this.user, this.adminUser, this.userDepartments)) {
                    this.$message.error(this.$t("tables.permissions.noPermission"));
                    return;
                }
                
                const newProtectionState = !table.deleteProtection;

                await TABLE_API.updateTable(table._id, {
                    deleteProtection: newProtectionState,
                });

                // Vuex sync et
                await this.syncTables(0);

                // Close dropdown
                this.$set(this.dropdownStates, table._id, false);

                // Show success message
                const messageText = newProtectionState ? "Silme koruması aktif edildi" : "Silme koruması kapatıldı";
                this.$message.success(messageText);
            } catch (error) {
                console.error("Error toggling delete protection:", error);
                this.$message.error("Koruma durumu güncellenirken hata oluştu");
            }
        },
        editTable(tableId) {
            const table = this.tables.find((table) => table._id === tableId);
            if (table) {
                // Tablo meta bilgisi düzenleme yetkisi kontrolü
                if (!checkTablePermission(table, TABLE_PERMISSION_SCOPES.TABLE_META_EDIT, this.user, this.adminUser, this.userDepartments)) {
                    this.$message.error(this.$t("tables.permissions.noPermission"));
                    return;
                }
                
                this.editingTable = { ...table };
                this.showEditModal = true;
            }
        },
        async handleUpdateTable(data) {
            try {
                await TABLE_API.updateTable(this.editingTable._id, data);
                this.showEditModal = false;
                // Vuex sync et
                await this.syncTables(0);
            } catch (error) {
                console.error("Error updating table:", error);
            }
        },
        prevPage() {
            if (this.pagination.page > 1) {
                this.pagination.page--;
            }
        },
        nextPage() {
            if (this.pagination.page < this.totalPages) {
                this.pagination.page++;
            }
        },
        performShowCreateCategoryModal() {
            this.showCreateCategoryModal = true;
        },
        closeCategoryModal() {
            this.showCreateCategoryModal = false;
            this.showEditCategoryModal = false;
            this.editingCategory = null;
            this.categoryForm = {
                name: "",
                icon: "",
            };
        },
        editCategory(category) {
            this.editingCategory = { ...category };
            this.showEditCategoryModal = true;
            this.categoryForm.name = category.name;
            this.categoryForm.icon = category.icon;
        },
        async saveCategory() {
            try {
                if (!this.categoryForm.name.trim()) {
                    this.$message.error("Kategori adı boş olamaz");
                    return;
                }

                // Check if category name already exists (except for current editing category)
                const existingInCategories = this.categories.find(
                    (cat) =>
                        cat.name === this.categoryForm.name &&
                        (!this.editingCategory || cat.name !== this.editingCategory.name)
                );

                const existingInTables = this.categorizedTables.find(
                    (cat) =>
                        cat.name === this.categoryForm.name &&
                        (!this.editingCategory || cat.name !== this.editingCategory.name)
                );

                if (existingInCategories || existingInTables) {
                    this.$message.error("Bu isimde bir kategori zaten mevcut");
                    return;
                }

                if (this.editingCategory) {
                    // Update existing category
                    await this.updateCategoryName(
                        this.editingCategory.name,
                        this.categoryForm.name,
                        this.categoryForm.icon
                    );

                    // Update in categories array
                    const categoryIndex = this.categories.findIndex((cat) => cat.name === this.editingCategory.name);
                    if (categoryIndex !== -1) {
                        this.$set(this.categories, categoryIndex, {
                            name: this.categoryForm.name,
                            icon: this.categoryForm.icon,
                        });
                    }
                } else {
                    // Create new category - add to categories array
                    this.categories.push({
                        name: this.categoryForm.name,
                        icon: this.categoryForm.icon,
                    });
                    this.$message.success("Kategori oluşturuldu. Tabloları bu kategoriye sürükleyebilirsiniz.");
                }

                this.closeCategoryModal();
            } catch (error) {
                console.error("Error saving category:", error);
                this.$message.error("Kategori kaydedilirken hata oluştu");
            }
        },
        async updateCategoryName(oldName, newName, newIcon) {
            try {
                // Find all tables with this category and update them
                const tablesToUpdate = this.tables.filter((table) => table.category && table.category.name === oldName);

                for (const table of tablesToUpdate) {
                    await TABLE_API.updateTable(table._id, {
                        category: {
                            name: newName,
                            icon: newIcon,
                        },
                    });
                }

                // Vuex sync et
                await this.syncTables(0);

                this.$message.success("Kategori güncellendi");
            } catch (error) {
                console.error("Error updating category:", error);
                this.$message.error("Kategori güncellenirken hata oluştu");
            }
        },
        async deleteCategory(categoryName) {
            try {
                await this.$confirm({
                    title: "Kategoriyi Sil",
                    content: `"${categoryName}" kategorisini silmek istediğinizden emin misiniz? Bu kategorideki tablolar "Genel" kategorisine taşınacak.`,
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: async () => {
                        // Find all tables with this category and move them to General
                        const tablesToUpdate = this.tables.filter(
                            (table) => table.category && table.category.name === categoryName
                        );

                        for (const table of tablesToUpdate) {
                            await TABLE_API.updateTable(table._id, {
                                category: {
                                    name: null,
                                    icon: null,
                                },
                            });
                        }

                        // Remove from categories array
                        this.categories = this.categories.filter((cat) => cat.name !== categoryName);

                        // Vuex sync et
                        await this.syncTables(0);

                        this.$message.success("Kategori silindi");
                    },
                });
            } catch (error) {
                console.error("Error deleting category:", error);
                this.$message.error("Kategori silinirken hata oluştu");
            }
        },
        onDragStart(event, table) {
            this.draggedTable = table;
            event.dataTransfer.effectAllowed = "move";
        },
        allowDrop(event, categoryName) {
            event.preventDefault();
            this.dragOverCategory = categoryName;
            event.dataTransfer.dropEffect = "move";
        },
        onDragLeave(event) {
            this.dragOverCategory = null;
        },
        onDragEnd(event) {
            this.dragOverCategory = null;
        },
        async onDrop(event, categoryName) {
            event.preventDefault();

            if (!this.draggedTable) return;

            try {
                const newCategory =
                    categoryName === "Genel"
                        ? { name: null, icon: null }
                        : { name: categoryName, icon: this.getCategoryIcon(categoryName) };

                await TABLE_API.updateTable(this.draggedTable._id, {
                    category: newCategory,
                });

                // Vuex sync et
                await this.syncTables(0);

                this.$message.success(`Tablo "${categoryName}" kategorisine taşındı`);
                this.draggedTable = null;
            } catch (error) {
                console.error("Error moving table:", error);
                this.$message.error("Tablo taşınırken hata oluştu");
            }
        },
        getCategoryIcon(categoryName) {
            // First check in separately created categories
            const separateCategory = this.categories.find((cat) => cat.name === categoryName);
            if (separateCategory) {
                return separateCategory.icon;
            }

            // Fallback to categorized tables
            const category = this.categorizedTables.find((cat) => cat.name === categoryName);
            return category ? category.icon : null;
        },
        toggleCategory(categoryName) {
            this.categoryExpandStates[categoryName] = !this.categoryExpandStates[categoryName];
        },
        async duplicateTable(table) {
            try {
                // Yetki kontrolü - Tabloyu görebilmeli
                if (!canAccessTable(table, this.user, this.adminUser, this.userDepartments)) {
                    this.$message.error(this.$t("tables.permissions.noPermission"));
                    return;
                }
                
                // Close dropdown
                this.$set(this.dropdownStates, table._id, false);
                
                const response = await TABLE_API.duplicateTable(table._id);
                // Vuex sync et
                await this.syncTables(0);
                this.$message.success("Tablo kopyalandı");
            } catch (error) {
                console.error("Error duplicating table:", error);
                this.$message.error("Tablo kopyalanırken hata oluştu");
            }
        },
        async copyTableAsJSON(table) {
            try {
                // Yetki kontrolü - Tabloyu görebilmeli
                if (!canAccessTable(table, this.user, this.adminUser, this.userDepartments)) {
                    this.$message.error(this.$t("tables.permissions.noPermission"));
                    return;
                }
                
                // Close dropdown
                this.$set(this.dropdownStates, table._id, false);
                
                const exportData = await TABLE_API.exportTableAsJSON(table._id);
                
                // Clipboard'a kopyala
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
                    this.$message.success("Tablo JSON olarak panoya kopyalandı. Başka müşteri panelinde Ctrl+V ile yapıştırabilirsiniz.");
                } else {
                    // Fallback: Create textarea and copy
                    const textArea = document.createElement('textarea');
                    textArea.value = JSON.stringify(exportData, null, 2);
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    this.$message.success("Tablo JSON olarak panoya kopyalandı (fallback method)");
                }
                
                // Clipboard kontrolü yap
                setTimeout(() => {
                    this.checkClipboard();
                }, 200);
            } catch (error) {
                console.error("Error copying table as JSON:", error);
                this.$message.error("Tablo JSON olarak kopyalanırken hata oluştu");
            }
        },
        async pasteTable() {
            try {
                let clipboardText;
                
                // Clipboard'dan text al
                if (navigator.clipboard) {
                    clipboardText = await navigator.clipboard.readText();
                } else {
                    this.$message.warning("Bu tarayıcıda clipboard erişimi desteklenmiyor");
                    return;
                }
                
                // JSON parse et
                let importData;
                try {
                    importData = JSON.parse(clipboardText);
                } catch (parseError) {
                    this.$message.warning("Panoda geçerli bir tablo JSON'u bulunamadı");
                    return;
                }
                
                // Supsis tablo export'u mu kontrol et
                if (!importData.supsis_table_export || importData.version !== "1.0") {
                    this.$message.warning("Panoda geçerli bir Supsis tablo export'u bulunamadı");
                    return;
                }
                
                const response = await TABLE_API.importTableFromJSON(importData);
                // Vuex sync et
                await this.syncTables(0);
                
                this.$message.success(`"${response.name}" tablosu başarıyla içeri aktarıldı`);
                
                // Clipboard'ı tekrar kontrol et
                setTimeout(() => {
                    this.checkClipboard();
                }, 1000);
            } catch (error) {
                console.error("Error pasting table:", error);
                this.$message.error("Tablo yapıştırılırken hata oluştu");
            }
        },
        handleKeyDown(event) {
            // Ctrl+V veya Cmd+V (Mac)
            if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
                // Input alanlarında değilse paste yap
                const activeElement = document.activeElement;
                const isInputElement = activeElement && (
                    activeElement.tagName === 'INPUT' || 
                    activeElement.tagName === 'TEXTAREA' ||
                    activeElement.contentEditable === 'true'
                );
                
                if (!isInputElement) {
                    event.preventDefault();
                    this.pasteTable();
                }
            }
        },
        
        async checkClipboard() {
            try {
                if (!navigator.clipboard) {
                    this.hasTableInClipboard = false;
                    return;
                }
                
                const clipboardText = await navigator.clipboard.readText();
                
                // JSON parse et
                let importData;
                try {
                    importData = JSON.parse(clipboardText);
                } catch (parseError) {
                    this.hasTableInClipboard = false;
                    return;
                }
                
                // Supsis tablo export'u mu kontrol et
                if (importData.supsis_table_export && importData.version === "1.0") {
                    this.hasTableInClipboard = true;
                } else {
                    this.hasTableInClipboard = false;
                }
            } catch (error) {
                // Permission denied veya başka bir hata
                this.hasTableInClipboard = false;
            }
        },
        
        handleWindowFocus() {
            // Kullanıcı başka yerden geldiğinde clipboard'ı kontrol et
            this.checkClipboard();
        },
        
        availableCategoriesForTable(table) {
            const currentCategory = table.category && table.category.name ? table.category.name : "Genel";
            
            return this.categorizedTables.filter(category => category.name !== currentCategory);
        },
        
        async moveTableToCategory(table, categoryName) {
            try {
                // Yetki kontrolü
                if (!checkTablePermission(table, TABLE_PERMISSION_SCOPES.TABLE_META_EDIT, this.user, this.adminUser, this.userDepartments)) {
                    this.$message.error(this.$t("tables.permissions.noPermission"));
                    return;
                }
                
                // Close dropdown
                this.$set(this.dropdownStates, table._id, false);
                
                const newCategory = categoryName === "Genel" 
                    ? { name: null, icon: null }
                    : { name: categoryName, icon: this.getCategoryIcon(categoryName) };

                await TABLE_API.updateTable(table._id, {
                    category: newCategory,
                });

                // Vuex sync et
                await this.syncTables(0);

                this.$message.success(`Tablo "${categoryName}" kategorisine taşındı`);
            } catch (error) {
                console.error("Error moving table:", error);
                this.$message.error("Tablo taşınırken hata oluştu");
            }
        },
        
        availableCategoriesForCategory(currentCategoryName) {
            return this.categorizedTables.filter(category => category.name !== currentCategoryName);
        },
        
        async moveAllTablesToCategory(fromCategoryName, toCategoryName) {
            try {
                // Close dropdown
                this.$set(this.categoryDropdownStates, fromCategoryName, false);
                
                const sourceCategory = this.categorizedTables.find(cat => cat.name === fromCategoryName);
                if (!sourceCategory || sourceCategory.tables.length === 0) {
                    this.$message.warning("Taşınacak tablo bulunamadı");
                    return;
                }
                
                const newCategory = toCategoryName === "Genel" 
                    ? { name: null, icon: null }
                    : { name: toCategoryName, icon: this.getCategoryIcon(toCategoryName) };

                // Update all tables in the category
                for (const table of sourceCategory.tables) {
                    await TABLE_API.updateTable(table._id, {
                        category: newCategory,
                    });
                }

                // Vuex sync et
                await this.syncTables(0);

                this.$message.success(`${sourceCategory.tables.length} tablo "${toCategoryName}" kategorisine taşındı`);
            } catch (error) {
                console.error("Error moving all tables:", error);
                this.$message.error("Tablolar taşınırken hata oluştu");
            }
        },
        goToPermissionSettings(table) {
            // Close dropdown
            this.$set(this.dropdownStates, table._id, false);
            
            // Yetki kontrolü
            if (!canEditTablePermissions(table, this.user, this.adminUser)) {
                this.$message.error("Bu tablonun gizlilik ayarlarını düzenleme yetkiniz bulunmamaktadır");
                return;
            }
            
            // Route'a yönlendir
            this.$router.push(`/tables/t/${table._id}/permissions`);
        },
        canShowPermissionSettings(table) {
            return canEditTablePermissions(table, this.user, this.adminUser);
        },
        canEditTableMeta(table) {
            return checkTablePermission(table, TABLE_PERMISSION_SCOPES.TABLE_META_EDIT, this.user, this.adminUser, this.userDepartments);
        },
        canAccessTable(table) {
            return canAccessTable(table, this.user, this.adminUser, this.userDepartments);
        },
    },
    created() {
        // Eğer store'da tablo yoksa sync yap
        // TablesHome zaten sync ediyor olabilir, gereksiz çift çağrıyı önle
        if (this.tables.length === 0) {
            this.fetchTables();
        }
        
        // Keyboard event listener ekle
        document.addEventListener('keydown', this.handleKeyDown);
        
        // Window focus event listener ekle
        window.addEventListener('focus', this.handleWindowFocus);
        
        // İlk clipboard kontrolü yap (biraz gecikme ile)
        setTimeout(() => {
            this.checkClipboard();
        }, 500);
    },
    destroyed() {
        // Event listener'ları temizle
        document.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('focus', this.handleWindowFocus);
    },
    watch: {
        categorizedTables: {
            handler(newCategories) {
                // Ensure all categories start expanded by default
                newCategories.forEach((category) => {
                    if (this.categoryExpandStates[category.name] === undefined) {
                        this.$set(this.categoryExpandStates, category.name, true);
                    }
                });
            },
            immediate: true,
        },
    },
};
</script>

<style scoped lang="scss">
.page-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px;

    .paste-info-message {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: linear-gradient(135deg, #e6f7ff, #bae7ff);
        border: 1px solid #40a9ff;
        border-radius: 8px;
        color: #1890ff;
        font-size: 14px;
        margin-bottom: 10px;
        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
        animation: slideInDown 0.3s ease-out;

        .material-icons {
            font-size: 20px;
            color: #40a9ff;
        }

        strong {
            color: #0050b3;
            font-weight: 600;
        }
    }

    @keyframes slideInDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;

                .copilot-icon {
                    vertical-align: middle;
                    margin-right: 0px;
                    width: 15px;
                    height: 15px;
                }

                .category-btn {
                    .material-icons {
                        margin-right: 5px;
                    }
                }
            }
        }

        .categories-container {
            display: flex;
            flex-direction: column;
            gap: 20px;

            .category-section {
                border: 1px solid #e8e8e8;
                border-radius: 8px;
                padding: 0;
                background: #fafafa;
                transition: all 0.3s ease;

                &:hover {
                    border-color: #d9d9d9;
                }

                .category-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 20px;
                    background: #f5f5f5;
                    border-bottom: 1px solid #e8e8e8;
                    border-radius: 8px 8px 0 0;
                    cursor: pointer;
                    transition: background-color 0.2s ease;

                    &:hover {
                        background: #eeeeee;
                    }

                    .category-info {
                        display: flex;
                        align-items: center;
                        gap: 10px;

                        .collapse-toggle {
                            padding: 4px !important;
                            min-width: 32px !important;
                            height: 32px !important;

                            .material-icons {
                                font-size: 20px;
                                transition: transform 0.3s ease;
                            }

                            &:hover .material-icons {
                                transform: scale(1.1);
                            }
                        }

                        .category-icon {
                            font-size: 24px;
                            color: #666;
                        }

                        .category-name {
                            font-size: 18px;
                            font-weight: 600;
                            color: #333;
                        }

                        .table-count {
                            font-size: 14px;
                            color: #999;
                        }
                    }

                    .category-actions {
                        display: flex;
                        gap: 5px;
                        align-items: center;
                        
                        .category-more-actions-btn {
                            opacity: 0.7;
                            transition: opacity 0.2s ease;
                            
                            &:hover {
                                opacity: 1;
                            }
                        }
                    }
                }

                .tables-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 15px;
                    padding: 20px;
                    transition: all 0.3s ease;
                    overflow: hidden;

                    &.collapsed {
                        max-height: 0;
                        padding: 0 20px;
                        opacity: 0;
                    }

                    .empty-category-message {
                        grid-column: 1 / -1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        padding: 40px 20px;
                        color: #999;
                        font-style: italic;
                        border: 2px dashed #e8e8e8;
                        border-radius: 8px;
                        background: #fafafa;

                        .material-icons {
                            font-size: 20px;
                        }
                    }

                    .table-card {
                        background: white;
                        border: 1px solid #e8e8e8;
                        border-radius: 8px;
                        padding: 15px;
                        cursor: grab;
                        transition: all 0.3s ease;

                        &:hover {
                            border-color: #1890ff;
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                        }

                        &:active {
                            cursor: grabbing;
                        }

                        .table-card-header {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-bottom: 10px;

                            .table-name {
                                margin: 0;
                                font-size: 16px;
                                font-weight: 600;
                                color: #1890ff;
                                cursor: pointer;
                                flex: 1;
                                text-decoration: none;
                                display: block;

                                &:hover {
                                    text-decoration: underline;
                                }
                            }

                            .table-actions {
                                display: flex;
                                gap: 5px;
                                margin-left: 10px;
                            }
                        }

                        .table-description {
                            color: #666;
                            font-size: 14px;
                            margin-bottom: 15px;
                            line-height: 1.4;
                        }

                        .table-meta {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            font-size: 12px;
                            color: #999;

                            .created-by {
                                display: flex;
                                align-items: center;
                                gap: 5px;

                                span {
                                    max-width: 100px;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                    white-space: nowrap;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .category-form {
        padding: 20px;

        .form-group {
            margin-bottom: 20px;

            label {
                display: block;
                margin-bottom: 8px;
                font-weight: 600;
                color: #333;
            }
        }

        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 30px;
        }
    }

    #tables-table {
        .actions {
            display: flex;
            gap: 5px;
            align-items: center;
        }

        .table-name-link {
            color: inherit;
            text-decoration: none;
            cursor: pointer;

            &:hover {
                text-decoration: underline !important;
            }
        }
    }

    .agent-detail {
        display: flex;
        gap: 5px;
    }
}

// Drag and drop styling
.category-section {
    &.drag-over {
        border-color: #1890ff !important;
        background-color: #f0f8ff !important;
    }
}

.table-card {
    &.dragging {
        opacity: 0.5;
        transform: rotate(5deg);
    }
}
</style>

<style lang="scss">
.table-actions-dropdown,
.category-actions-dropdown {
    .ant-popover-inner-content {
        padding: 0 !important;
    }

    .dropdown-menu-content {
        min-width: 200px;

        .dropdown-menu-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 16px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            border-bottom: 1px solid #f0f0f0;
            position: relative;

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background-color: #f5f5f5;
            }

            .material-icons {
                font-size: 18px;
                color: #666;
            }

            span:not(.submenu-arrow):not(.material-icons) {
                color: #333;
                font-size: 14px;
                flex: 1;
            }
            
            &.submenu-item {
                position: relative;
                
                .submenu-arrow {
                    margin-left: auto;
                    font-size: 16px;
                    color: #999;
                }
                
                .submenu-content {
                    position: absolute;
                    right: 100%;
                    top: 0;
                    min-width: 180px;
                    background: white;
                    border: 1px solid #e8e8e8;
                    border-radius: 6px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    opacity: 0;
                    visibility: hidden;
                    transform: translateX(10px);
                    transition: all 0.2s ease;
                    z-index: 1000;
                    
                    .submenu-item-content {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 12px 16px;
                        cursor: pointer;
                        transition: background-color 0.2s ease;
                        border-bottom: 1px solid #f0f0f0;
                        
                        &:last-child {
                            border-bottom: none;
                        }
                        
                        &:hover {
                            background-color: #f5f5f5;
                        }
                        
                        .material-icons {
                            font-size: 16px;
                            color: #666;
                        }
                        
                        span {
                            color: #333;
                            font-size: 14px;
                        }
                    }
                }
                
                &:hover .submenu-content {
                    opacity: 1;
                    visibility: visible;
                    transform: translateX(0);
                }
            }
        }
    }
}
.table-category-form {
    padding: 20px;

    .form-group {
        margin-bottom: 20px;

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #333;
        }
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 30px;
    }
}
</style>
